import {
	ActionIcon,
	Alert,
	Button,
	Center,
	Loader,
	Modal,
	Stack,
	Text,
	UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconChevronLeft } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router";

import { useAuth } from "@/features/auth/lib/use-auth";
import {
	fetchCardCheck,
	fetchCardRewards,
	redeemReward,
} from "@/features/card/api/card-api";
import type { CardReward } from "@/features/card/api/types";
import { isCardCode } from "@/features/card/lib/card-code";
import { redeemErrorCopy } from "@/features/card/lib/redeem-error";
import { CardShell } from "@/features/card/ui/card-shell";

export function CatalogPage() {
	const { code } = useParams<{ code: string }>();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { canRedeem } = useAuth();
	const queryClient = useQueryClient();
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const idempotencyKey = useRef<string | null>(null);

	const validCode = isCardCode(code);

	const cardQuery = useQuery({
		queryKey: ["card", code],
		queryFn: () => fetchCardCheck(code as string),
		enabled: validCode,
		staleTime: 0,
		gcTime: 0,
		refetchOnWindowFocus: true,
	});

	const rewardsQuery = useQuery({
		queryKey: ["card-rewards", code],
		queryFn: () => fetchCardRewards(code as string),
		enabled: validCode && cardQuery.data?.status === "VALID",
		staleTime: 0,
		gcTime: 0,
		refetchOnWindowFocus: true,
	});

	const redeem = useMutation({
		mutationFn: (input: { rewardId: string; idempotencyKey: string }) =>
			redeemReward(code as string, input),
		onSuccess: async (result) => {
			setConfirmOpen(false);
			idempotencyKey.current = null;
			notifications.show({
				color: "green",
				title: t("catalog.success", { count: result.pointsCost }),
				message: t("catalog.successDetail", {
					name: result.reward,
					balance: result.bonusBalance,
				}),
			});
			await queryClient.invalidateQueries({ queryKey: ["card", code] });
			await queryClient.invalidateQueries({ queryKey: ["card-rewards", code] });
			navigate(`/${code}`, { replace: true });
		},
		onError: (error: Error & { code?: string }) => {
			const copy = redeemErrorCopy(error.code);
			notifications.show({
				color: "red",
				title: t(copy.titleKey),
				message: t(copy.messageKey),
			});
			void rewardsQuery.refetch();
		},
	});

	if (!validCode) {
		return <Navigate to="/" replace />;
	}

	if (!canRedeem) {
		return <Navigate to={`/${code}`} replace />;
	}

	if (cardQuery.isLoading) {
		return (
			<CardShell title={t("catalog.title")}>
				<Center py="xl">
					<Loader />
				</Center>
			</CardShell>
		);
	}

	if (cardQuery.data?.status !== "VALID") {
		return <Navigate to={`/${code}`} replace />;
	}

	const rewards = rewardsQuery.data ?? [];
	const selected = rewards.find((item) => item.id === selectedId) ?? null;
	const canSubmit = Boolean(selected?.affordable) && !redeem.isPending;

	const openConfirm = () => {
		if (!idempotencyKey.current) {
			idempotencyKey.current = crypto.randomUUID();
		}
		setConfirmOpen(true);
	};

	const confirm = () => {
		if (!selected || !idempotencyKey.current) {
			return;
		}
		redeem.mutate({
			rewardId: selected.id,
			idempotencyKey: idempotencyKey.current,
		});
	};

	return (
		<CardShell
			title={t("catalog.title")}
			leading={
				<ActionIcon
					variant="subtle"
					color="gray"
					onClick={() => navigate(`/${code}`)}
					aria-label="back"
				>
					<IconChevronLeft />
				</ActionIcon>
			}
			footer={
				<Button
					fullWidth
					size="md"
					disabled={!canSubmit}
					loading={redeem.isPending}
					onClick={openConfirm}
				>
					{selected
						? t("catalog.redeemNamed", { count: selected.pointsCost })
						: t("check.redeem")}
				</Button>
			}
		>
			<Stack gap="xs" pt="sm">
				{rewardsQuery.isError ? (
					<Alert color="red" title={t("catalog.loadError")}>
						<Stack gap="sm">
							<Text size="sm">{t("errors.network")}</Text>
							<Button
								variant="light"
								onClick={() => void rewardsQuery.refetch()}
							>
								{t("common.retry")}
							</Button>
						</Stack>
					</Alert>
				) : null}
				{rewardsQuery.isLoading ? (
					<Center py="xl">
						<Loader />
					</Center>
				) : rewards.length === 0 && !rewardsQuery.isError ? (
					<Alert color="gray" title={t("catalog.emptyTitle")}>
						{t("catalog.empty")}
					</Alert>
				) : (
					rewards.map((item) => (
						<RewardRow
							key={item.id}
							item={item}
							selected={item.id === selectedId}
							onSelect={() => {
								if (item.affordable) {
									setSelectedId(item.id);
								}
							}}
						/>
					))
				)}
			</Stack>

			<Modal
				opened={confirmOpen}
				onClose={() => {
					if (!redeem.isPending) {
						setConfirmOpen(false);
					}
				}}
				title={t("catalog.confirmTitle")}
				centered
			>
				<Stack>
					<Text>
						{t("catalog.confirm", { count: selected?.pointsCost ?? 0 })}
					</Text>
					{selected ? (
						<Text fw={600}>
							{t("catalog.confirmItem", { name: selected.reward })}
						</Text>
					) : null}
					<Button
						variant="default"
						disabled={redeem.isPending}
						onClick={() => setConfirmOpen(false)}
					>
						{t("catalog.no")}
					</Button>
					<Button
						loading={redeem.isPending}
						disabled={redeem.isPending}
						onClick={confirm}
					>
						{t("catalog.yes")}
					</Button>
				</Stack>
			</Modal>
		</CardShell>
	);
}

function RewardRow({
	item,
	selected,
	onSelect,
}: {
	item: CardReward;
	selected: boolean;
	onSelect: () => void;
}) {
	const { t } = useTranslation();
	return (
		<UnstyledButton
			onClick={onSelect}
			disabled={!item.affordable}
			w="100%"
			p="md"
			style={{
				borderRadius: 12,
				border: selected
					? "2px solid var(--mantine-primary-color-filled)"
					: "1px solid var(--card-row-border)",
				opacity: item.affordable ? 1 : 0.45,
				background: "var(--card-device-bg)",
			}}
		>
			<Text fw={600}>{item.reward}</Text>
			<Text size="sm" c="dimmed">
				{t("catalog.cost", { count: item.pointsCost })}
			</Text>
		</UnstyledButton>
	);
}
